/*
 * `?sessionless=1` is the hint Forms adds to the DigiSign hand-off URL when Forms itself runs on a
 * widget token (framed, no Okta cookie session). Only then do we ask the widget for a token. Without
 * it we sign in the normal way, even inside the widget.
 *
 * The hint only counts next to a valid `frameOrigin`, and only while framed. auth-js rewrites the
 * entry URL as it starts, so we read the hint once at boot and keep it in sessionStorage, like
 * frameOrigin, so reloads of the frame keep it. A later entry URL with a frameOrigin but no hint is
 * a new cookie-mode hand-off and clears it.
 */
import { FRAME_ORIGIN_PARAM, normalizeFrameOrigin } from './frame-origin';
import { isAppRunningInIframe } from './is-app-running-in-iframe';

export const SESSIONLESS_PARAM = 'sessionless';

const SESSIONLESS_STORAGE_KEY = 'digisignSessionless';

// In-memory copy for this document, for browsers where sessionStorage throws.
let rememberedHint = false;

/** Forgets the hint (standalone load, a cookie-mode hand-off, or tests). */
export function forgetSessionlessHint(): void {
  rememberedHint = false;
  try {
    sessionStorage.removeItem(SESSIONLESS_STORAGE_KEY);
  } catch {
    // Storage blocked: nothing stored there to forget.
  }
}

/** Reads the hint from the entry URL and remembers or clears it. Call once at boot. */
export function captureSessionlessHint(search: string = window.location.search): void {
  if (!isAppRunningInIframe()) {
    forgetSessionlessHint();
    return;
  }
  const params = new URLSearchParams(search);
  // Without a valid frameOrigin this URL says nothing about the widget, so keep what we have.
  if (!normalizeFrameOrigin(params.get(FRAME_ORIGIN_PARAM))) return;

  if (params.get(SESSIONLESS_PARAM) !== '1') {
    forgetSessionlessHint();
    return;
  }
  rememberedHint = true;
  try {
    sessionStorage.setItem(SESSIONLESS_STORAGE_KEY, '1');
  } catch {
    // Storage blocked: the in-memory copy still serves this document.
  }
}

/** True when Forms told us, for this frame, that it runs on a widget token. */
export function isSessionlessHinted(): boolean {
  if (!isAppRunningInIframe()) return false;
  if (rememberedHint) return true;
  try {
    return sessionStorage.getItem(SESSIONLESS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Adds the hint to a URL for this frame's address bar, so a reload still carries it. Returns the
 * href unchanged when there is no hint or it already has one. Keeps absolute hrefs absolute, like
 * withFrameOrigin.
 */
export function withSessionlessHint(href: string): string {
  if (!isSessionlessHinted()) return href;

  let url: URL;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return href;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return href;
  if (url.searchParams.get(SESSIONLESS_PARAM) === '1') return href;

  url.searchParams.set(SESSIONLESS_PARAM, '1');
  const isAbsolute = /^https?:\/\//i.test(href);
  return isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
}
